import { Avatar, AvatarFallback, AvatarImage, AvatarGroup, AvatarGroupCount } from '../ui/avatar';
import { Button } from '../ui/button';
import { ArrowLeft } from '@/components/icons';
import { useBackOrHome } from './useBackOrHome';
import { useCrewGetByIds } from '../../lib/hooks';

interface TopBarChatProps {
  participantName: string;
  participantCrewIds?: string[];
  participantImageUrl?: string;
  participantInitials: string;
  participantAvatarColor?: string;
  subtitle?: string;
  onBack?: () => void;
  isGroup?: boolean;
}

export function TopBarChat({
  participantName,
  participantCrewIds = [],
  participantImageUrl,
  participantInitials,
  participantAvatarColor,
  subtitle,
  onBack,
  isGroup = false,
}: TopBarChatProps) {
  const backOrHome = useBackOrHome();
  const handleBack = onBack ?? backOrHome;
  // Fetch participant details for group conversations (limit to 3 for display)
  const { data: participants = [] } = useCrewGetByIds(
    isGroup ? participantCrewIds.slice(0, 3) : []
  );

  const renderAvatars = () => {
    if (isGroup && participants.length > 0) {
      const remainingCount = participantCrewIds.length - 3 + 1; // +1 for the "+X" avatar

      return (
        <AvatarGroup>
          {participants.slice(0, 3).map((participant: any, index: number) => {
            const initials = participant.name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || '??';
            const bgColor = participant.avatarColor;

            return (
              <Avatar key={participant.id || index} className="size-8 border-none"><AvatarFallback className="text-[12px] font-semibold text-background" style={bgColor ? { backgroundColor: bgColor } : undefined}>{initials}</AvatarFallback></Avatar>
            );
          })}
          {remainingCount > 0 && (
            <AvatarGroupCount className="w-8 h-8 text-[12px] font-semibold">
              +{remainingCount}
            </AvatarGroupCount>
          )}
        </AvatarGroup>
      );
    }

    // Single avatar for DMs
    return (
      <Avatar className="w-8 h-8">
        {participantImageUrl ? <AvatarImage src={participantImageUrl} /> : null}
        <AvatarFallback className="text-[16px] font-semibold text-background" style={participantAvatarColor ? { backgroundColor: participantAvatarColor } : undefined}>{participantInitials}</AvatarFallback>
      </Avatar>
    );
  };

  return (
    <>
      {/* Spacer to reserve space in document flow — must match fixed header height */}
      <div className="shrink-0 safe-top">
        <div className="h-16" />
      </div>
      {/* Fixed header */}
      <div className="bg-surface-brand-L0 fixed top-0 left-0 right-0 z-50 safe-top">
        <div className="content-frame px-gutter py-4 flex items-center gap-3">
          {/* Back button */}
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            onClick={handleBack}
            className="size-8 shrink-0 border-none bg-transparent"
          >
            <ArrowLeft className="size-5 text-content-primary-bold" />
          </Button>

          {/* Avatar + Name */}
          {renderAvatars()}
          <div className="flex flex-col">
            <h1 className="text-content-primary-bold text-lg font-semibold leading-tight">
              {participantName}
            </h1>
            {subtitle && (
              <span className="text-content-primary-subtle text-sm leading-tight">
                {subtitle}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
